import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Chrome } from './components/Chrome'
import { SessionContext, isUnauthenticated, loginPathFor, useCurrentUser } from './lib/session'
import { Login } from './routes/Login'
import { Queue } from './routes/Queue'
import { NewCase } from './routes/NewCase'
import { CasePage } from './routes/CasePage'
import { Settings } from './routes/Settings'
import { Templates, TemplateEditor } from './routes/Templates'
import { Process } from './routes/Process'
import { PageMap } from './routes/PageMap'
import { Papers } from './routes/Papers'
import { OcrTool } from './routes/OcrTool'
import { Stub } from './routes/Stub'
import { Workbench } from './routes/Workbench'
import { Audit, CaseAudit } from './routes/Audit'
import { Accounts, ChangeMyPassword } from './routes/Accounts'

/**
 * Everything except `/login` is behind the session. A 401 anywhere sends the
 * officer to the login screen **carrying the path they were on**, so signing in
 * again puts them back where they were rather than on the queue.
 */
export function App() {
  const location = useLocation()
  const me = useCurrentUser()

  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    )
  }

  if (me.isLoading) {
    return <div className="empty">Loading…</div>
  }

  if (!me.data || isUnauthenticated(me.error)) {
    return <Navigate to={loginPathFor(location)} replace />
  }

  return (
    <SessionContext.Provider value={{ user: me.data, loading: false }}>
      <Chrome user={me.data}>
        <Routes>
          <Route path="/" element={<Navigate to="/cases" replace />} />
          <Route path="/cases" element={<Queue />} />
          <Route path="/cases/new" element={<NewCase />} />
          <Route path="/cases/:caseId" element={<CasePage />} />
          <Route path="/cases/:caseId/audit" element={<CaseAudit />} />
          <Route path="/cases/:caseId/documents/:documentType" element={<Workbench />} />
          <Route path="/cases/:caseId/bundles/:jobId" element={<PageMap />} />
          <Route path="/cases/:caseId/papers" element={<Papers />} />
          <Route path="/accounts" element={<Accounts />} />
          {/* Reachable by every role, including the CGM: an officer who could not
              change their own password would be an oversight, not a boundary. */}
          <Route path="/account/password" element={<ChangeMyPassword />} />
          <Route path="/ocr-tool" element={<OcrTool />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/templates/:documentType" element={<TemplateEditor />} />
          <Route path="/process" element={<Process />} />
          <Route path="/settings" element={<Settings />} />
          <Route
            path="/corpus"
            element={
              /*
               * **No phase, deliberately.** This said "Built in P10" until P10
               * merged without it: P10 is the admin editors, and the master plan
               * assigns the corpus browser to no phase at all — S-04 says the
               * PDFs are browsable and P11 adds FTS over them for chat, which is
               * not the same screen. A stub naming a phase that has shipped is a
               * worse answer than one naming none, and guessing P11 here would
               * put a roadmap decision in a render path (open item 59).
               */
              <Stub
                title="Corpus"
                what="The rule source — the statutory text every [S] block is reproduced from, with its verification state. Until it is verified, every statutory block carries its unverified marker. No phase is assigned to this screen yet."
              />
            }
          />
          <Route path="/audit" element={<Audit />} />
          <Route path="*" element={<Stub title="Not found" what="No such screen." />} />
        </Routes>
      </Chrome>
    </SessionContext.Provider>
  )
}
